import React, { FC, useContext, useState } from 'react'
import { Column } from 'react-table'
import { t } from '@lingui/macro'
import {
  MediaImpressionsOverviewOrderColumn,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import TableExtendedOld from '../../../components/common/table-extended-old/TableExtendedOld'
import { formatTimeInSec, getPagesCount } from '../utils'
import { formatDateShort } from '../../../utils/dateHelpers'
import { useMediaDetailImpressionsOverviewQuery } from './MediaDetailAnalytics.codegen'
import { DownloadMediaDetailCSV } from './DownloadMediaDetailCSV'
import { AnalyticsContext } from '../AnalyticsContext'

export const MediaDetailOverviewTable: FC<{
  topicElementId: number
}> = ({ topicElementId }) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewTableFilter, setVideosTableFilters] = useState({
    limit: 20,
    orderColumn: MediaImpressionsOverviewOrderColumn.engagementPercent,
    orderDirection: SqlOrderDirectionEnum.DESC
  })

  const overviewTableQuery = {
    ...overviewTableFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useMediaDetailImpressionsOverviewQuery({
    fetchPolicy: 'no-cache',
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      overviewTableFilter: overviewTableQuery,
      topicElementId
    }
  })

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`User name`,
      id: MediaImpressionsOverviewOrderColumn.displayName,
      accessor: 'topicEnrolment.user.display_name',
      Cell: (row) => <strong className="dark">{row.value}</strong>
    },
    {
      Header: t`Engagement`,
      maxWidth: 150,
      id: MediaImpressionsOverviewOrderColumn.engagementPercent,
      accessor: 'engagementPercent',
      Cell: (row) => `${row.value}%`
    },
    {
      Header: t`Play time`,
      maxWidth: 150,
      id: MediaImpressionsOverviewOrderColumn.playTimeSeconds,
      accessor: 'total_play_time_ms',
      Cell: (row) => formatTimeInSec(row.value / 1000)
    },
    {
      Header: t`Last activity`,
      maxWidth: 130,
      id: MediaImpressionsOverviewOrderColumn.updatedAt,
      accessor: 'lastActiveAt',
      Cell: (row) => formatDateShort(row.value)
    }
  ]

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.media.impressionsOverview?.count || 0,
      overviewTableQuery.limit
    )
  }

  return (
    <TableExtendedOld
      loading={loading}
      manual
      data={
        data &&
        data.analytics &&
        data.analytics.media.impressionsOverview?.items
      }
      tableHeader={t`User activity`}
      columns={tableColumns}
      className="-highlight AnalyticsTable"
      defaultPageSize={overviewTableQuery.limit}
      showPageSizeOptions={false}
      showSearchFilter
      resizable={false}
      minRows={5}
      pages={pagesCount}
      defaultSortDesc
      defaultSorted={[
        {
          id: overviewTableQuery.orderColumn,
          desc: overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
        }
      ]}
      onSortedChange={(newSorted) => {
        const newOrder = newSorted[0].id as MediaImpressionsOverviewOrderColumn

        setVideosTableFilters((prevState) => {
          const descendingOrder = newSorted[0] ? newSorted[0].desc : false

          return {
            ...prevState,
            orderColumn: newOrder as MediaImpressionsOverviewOrderColumn,
            orderDirection: descendingOrder
              ? SqlOrderDirectionEnum.DESC
              : SqlOrderDirectionEnum.ASC
          }
        })
      }}
      onPageChange={(newPage) => {
        changeFilter({ page: newPage + 1 })
      }}
      defaultSearch={overviewTableQuery.search}
      page={overviewTableQuery.page - 1}
      onSearchChange={(searchVal) => {
        changeFilter({ search: searchVal })
      }}
      paginationContentAfter={
        data && (
          <DownloadMediaDetailCSV
            title={`${t`Export data`} (.csv)`}
            topicElementId={topicElementId}
            mediaOverviewFilter={overviewTableQuery}
            mediaCount={data.analytics.media.impressionsOverview?.count || 0}
          />
        )
      }
    />
  )
}
